import { Controller } from "@hotwired/stimulus";

// Soft cascade: changes to Country narrow State options to states within
// that country. Selecting State without Country still works (shows all
// states + a small notice). Strict cascade is a v2 deferral.
//
// Markup:
//   <div data-controller="filter-country-state"
//        data-filter-country-state-data-url-value="/countries_states.json">
//     <select name="country" data-filter-country-state-target="country" multiple>
//       ...countries...
//     </select>
//     <select name="state" data-filter-country-state-target="state" multiple>
//       ...all states...
//     </select>
//     <div data-filter-country-state-target="notice" hidden>
//       States from any country. Set Country to narrow options.
//     </div>
//   </div>
export default class extends Controller {
  static targets = ["country", "state", "notice"];
  static values = { dataUrl: String };

  async connect() {
    this._allStateOptions = Array.from(this.stateTarget.options).map((o) => o.cloneNode(true));
    this._dict = null; // gate: update() no-ops until fetch resolves
    // rAF-coalesce: Select2 + filter_select2 wrapper fire both jQuery and
    // native change events on the country <select> for each option select,
    // and rapid multi-select interactions can trigger 80+ change events
    // per single user action. Each update() rebuilds the state dropdown
    // (DOM clone+remove+append) and triggers Select2 re-render on state.
    // Coalescing to one update() per frame brings 80×15ms down to ~15ms.
    this._pendingFrame = null;
    this._onCountryChange = () => {
      if (this._pendingFrame != null) return;
      this._pendingFrame = requestAnimationFrame(() => {
        this._pendingFrame = null;
        this.update();
      });
    };
    this.countryTarget.addEventListener("change", this._onCountryChange);
    // Race-fix: listener is attached BEFORE the await so a user change during
    // fetch is not lost. update() short-circuits while _dict is null; we call
    // update() again after the await to seed with the dict in place.

    try {
      const res = await fetch(this.dataUrlValue);
      this._dict = await res.json();
    } catch {
      this._dict = {};
    }
    this.update();
  }

  disconnect() {
    if (this._onCountryChange) {
      this.countryTarget.removeEventListener("change", this._onCountryChange);
    }
    if (this._pendingFrame != null) {
      cancelAnimationFrame(this._pendingFrame);
      this._pendingFrame = null;
    }
  }

  update() {
    if (this._dict === null) return; // fetch still in flight; defer
    const selectedCountries = Array.from(this.countryTarget.selectedOptions).map((o) => o.value);
    if (selectedCountries.length === 0) {
      // Restore full list + show notice
      this._setStateOptions(this._allStateOptions);
      if (this.hasNoticeTarget) this.noticeTarget.hidden = false;
      return;
    }
    if (this.hasNoticeTarget) this.noticeTarget.hidden = true;

    const allowed = new Set();
    selectedCountries.forEach((c) => {
      const states = this._dict[c] || [];
      states.forEach((s) => allowed.add(s));
    });

    const filtered = this._allStateOptions.filter((opt) => allowed.has(opt.value));
    this._setStateOptions(filtered);
  }

  _setStateOptions(opts) {
    const prevSelected = new Set(Array.from(this.stateTarget.selectedOptions).map((o) => o.value));
    // Project convention blocks innerHTML — clear via DOM removal.
    while (this.stateTarget.firstChild) this.stateTarget.removeChild(this.stateTarget.firstChild);
    opts.forEach((opt) => {
      const clone = opt.cloneNode(true);
      clone.selected = prevSelected.has(clone.value);
      this.stateTarget.appendChild(clone);
    });
    if (window.$ && window.$.fn.select2) window.$(this.stateTarget).trigger("change");
  }
}
